import {
  CONTAINER_EQUIPMENT,
  CombatStance,
  DEFAULT_STANCE,
  EQUIPMENT_SLOTS,
  ItemSlot,
  SLOT_NAMES,
  STANCES,
  type InventoryEntry,
  type InventoryPacket,
} from '@game/protocol';

import { hideItemTooltip } from './item-tooltip.ts';
import { collapsiblePanel, type PanelHeader } from './panel.ts';
import { clearDropHighlight, itemSlot, onIconsChanged, type SlotHandlers } from './slot.ts';

/**
 * O que o personagem está vestindo (PRD §6), em painel próprio.
 *
 * ## Por que saiu do inventário
 *
 * Os dez slots do corpo moravam no topo da janela de inventário, numa grade de cinco colunas
 * por duas linhas — a mesma grade dos contêineres, com os mesmos quadrados, coladas uma na
 * outra. Elas parecem a mesma coisa e não são: a mochila é uma **lista** de o que se carrega,
 * e não tem forma; o equipamento é um **retrato** de onde cada peça fica no corpo, e a forma é
 * a informação. Enfileirado em cinco colunas, "Mão direita" caía embaixo de "Corpo" e ao lado
 * de "Mão esquerda" por acidente de aritmética, e o jogador tinha que ler o nome de cada
 * quadrado para saber onde estava o elmo.
 *
 * ## O boneco
 *
 * Aqui eles ficam em torno de um eixo central, na posição que ocupam no corpo:
 *
 *     amuleto  cabeça  mochila
 *     arma     corpo   escudo
 *     anel     pernas  munição
 *              pés
 *
 * A leitura passa a ser espacial: o elmo está em cima porque a cabeça está em cima, a arma
 * está à esquerda porque é a mão que a segura. É o arranjo que o gênero usa há vinte e cinco
 * anos, e ele pegou porque funciona — o olho encontra o slot sem soletrar o rótulo.
 *
 * Com painel próprio o equipamento também ganha o que não cabia dentro do inventário: piso de
 * altura só dele na coluna, e a barra de título que recolhe (ver `panel.ts`). Quem está
 * arrumando a mochila recolhe o boneco; quem está trocando de armadura recolhe a mochila.
 *
 * ## A postura, embaixo do boneco
 *
 * A escolha de **como** lutar (PRD §5, ver `CombatStance`) mora aqui e não no painel de
 * status, e a razão é que ela pertence à mesma pergunta que o boneco responde. Espada e
 * escudo são com o que se luta; postura é o que se faz com eles — trocar de arma e trocar de
 * postura são o mesmo gesto de "vou encarar isso de outro jeito", e quem acabou de guardar o
 * escudo para pegar o arco está a um centímetro do botão que diz para não ficar parado.
 *
 * No status ela ficaria ao lado de vida, mana e experiência, que são coisas que **acontecem
 * com** o personagem e que ele lê; a postura é a única linha daquela coluna em que ele
 * decide algo. E o status é o painel mais alto dos cinco: numa janela apertada, a decisão
 * seria o que sai da vista primeiro.
 *
 * ## O que ele **não** faz
 *
 * O mesmo que o inventário não faz: decidir se a peça pode ser vestida. Ele pede o movimento,
 * o servidor responde com um inventário novo. Prever aqui qual item cabe em qual slot criaria
 * duas cópias da regra — e a do cliente estaria errada no primeiro item novo.
 *
 * A postura segue a mesma disciplina: o clique pede, e o botão só acende quando o `Stats` de
 * volta confirma qual postura está em vigor. Acender no clique daria uma tela que discorda do
 * servidor sempre que o pacote se perder — e a postura é justamente a informação que precisa
 * estar certa quando o jogador decide se aguenta mais um golpe.
 */

/**
 * Os dez slots na ordem em que aparecem na tela, linha por linha.
 *
 * A ordem do DOM é a ordem visual de propósito, e não a ordem numérica do protocolo: é ela
 * que o Tab e o leitor de tela percorrem, e um foco que pula do amuleto para a munição porque
 * `Ammo` é o índice 9 seria um caminho que ninguém enxerga.
 *
 * Quem coloca cada célula no lugar é `grid-area` (ver `.equipment-doll` em `style.css`), e
 * não esta lista. Os pés são o único slot da última linha e ficariam encostados à esquerda se
 * a posição saísse da ordem.
 */
const DOLL: readonly ItemSlot[] = [
  ItemSlot.Amulet,
  ItemSlot.Head,
  ItemSlot.Backpack,
  ItemSlot.Weapon,
  ItemSlot.Armor,
  ItemSlot.Shield,
  ItemSlot.Ring,
  ItemSlot.Legs,
  ItemSlot.Ammo,
  ItemSlot.Feet,
];

/** Nome da área do grid por slot. Casa com `grid-template-areas` em `style.css`. */
const AREAS: Readonly<Record<ItemSlot, string>> = {
  [ItemSlot.Head]: 'cabeca',
  [ItemSlot.Amulet]: 'amuleto',
  [ItemSlot.Backpack]: 'mochila',
  [ItemSlot.Armor]: 'corpo',
  [ItemSlot.Weapon]: 'arma',
  [ItemSlot.Shield]: 'escudo',
  [ItemSlot.Legs]: 'pernas',
  [ItemSlot.Feet]: 'pes',
  [ItemSlot.Ring]: 'anel',
  [ItemSlot.Ammo]: 'municao',
};

/** As três posturas na ordem em que aparecem: da mais agressiva à mais cautelosa. */
const STANCE_ORDER: readonly CombatStance[] = [
  CombatStance.Offensive,
  CombatStance.Balanced,
  CombatStance.Defensive,
];

export interface EquipmentHandlers extends SlotHandlers {
  /** O jogador escolheu uma postura. Quem confirma é o `Stats` de volta. */
  onStance(stance: CombatStance): void;
}

export class EquipmentPanel {
  private readonly root: HTMLElement;
  private readonly handlers: EquipmentHandlers;
  private header: PanelHeader | null = null;

  private entries: InventoryEntry[] = [];

  /** A postura que o servidor confirmou. Ver `setStance`. */
  private stance: CombatStance = DEFAULT_STANCE;
  /** Os botões, para acender o certo sem redesenhar a fileira. */
  private readonly stanceButtons = new Map<CombatStance, HTMLButtonElement>();

  constructor(root: HTMLElement, handlers: EquipmentHandlers) {
    this.root = root;
    this.handlers = handlers;
    this.root.classList.add('equipment');

    // O ícone chega depois da montagem (ver `setItemIconResolver`): sem este registro, o
    // boneco ficaria com as três primeiras letras do nome de cada peça até o primeiro
    // movimento de item.
    onIconsChanged(() => this.render());
    this.render();
  }

  toggle(): void {
    this.header?.toggle();
  }

  /**
   * Recebe o **mesmo** pacote do inventário e fica com a parte dele que é do corpo.
   *
   * Um pacote só para os dois painéis, e não um por painel: vestir uma armadura muda os dois
   * ao mesmo tempo — sai da mochila, entra no corpo —, e dois pacotes abririam a janela em
   * que um chegou e o outro não, com a peça desenhada nos dois lugares ou em nenhum.
   */
  update(packet: InventoryPacket): void {
    this.entries = packet.entries;
    this.render();
  }

  /**
   * A postura que o servidor diz estar em vigor, vinda do `Stats`.
   *
   * Não redesenha o painel: o `Stats` chega a cada nível, a cada golpe recebido e a cada
   * minuto de saciedade, e reconstruir o boneco por causa disso mataria o slot sob o cursor
   * dezenas de vezes por minuto — exatamente o que o `hideItemTooltip` do `render` existe
   * para remediar quando não há escolha. Aqui há: são três classes de CSS.
   */
  setStance(stance: CombatStance): void {
    this.stance = stance;
    this.paintStance();
  }

  private render(): void {
    // Mesmo motivo do inventário: o `innerHTML = ''` mata o slot sob o cursor sem disparar
    // `mouseleave`, e a dica dele ficaria órfã na tela; o realce de destino do arraste sai
    // pelo mesmo motivo, porque o elemento aceso deixa o documento.
    hideItemTooltip();
    clearDropHighlight();
    this.root.innerHTML = '';

    this.header = collapsiblePanel(this.root, 'equipamento', 'Equipamento');
    this.root.append(this.header.root);

    const doll = document.createElement('div');
    doll.className = 'equipment-doll';

    for (const slot of DOLL) {
      const cell = this.cell(slot);
      cell.style.gridArea = AREAS[slot];
      doll.append(cell);
    }

    this.root.append(doll);

    // Slot que o protocolo declarou e o boneco não posiciona cai numa fila embaixo, em vez de
    // sumir. `EQUIPMENT_SLOTS` e `DOLL` são duas listas do mesmo fato, e a que vive no
    // protocolo é a que manda: no dia em que um slot de capa entrar em `ItemSlot`, ele
    // aparece aqui torto até alguém lhe dar um lugar no corpo — que é um defeito visível,
    // e não uma peça que o jogador equipa e não encontra mais.
    const extras = document.createElement('div');
    extras.className = 'equipment-extra';

    for (let slot = 0; slot < EQUIPMENT_SLOTS; slot++) {
      if (DOLL.includes(slot as ItemSlot)) continue;
      extras.append(this.cell(slot));
    }

    if (extras.childElementCount > 0) this.root.append(extras);

    this.root.append(this.stanceRow());
  }

  /**
   * A fileira de postura, embaixo do boneco.
   *
   * Três botões visíveis ao mesmo tempo, e não um botão que cicla entre as posturas. Ciclar
   * economizaria duas fileiras de 22 px e cobraria caro em outro lugar: o jogador que precisa
   * de defesa **agora**, com meia barra de vida, teria que descobrir quantos cliques faltam
   * até chegar nela — e descobriria passando pela postura ofensiva no caminho. Aqui a
   * distância entre "estou apanhando" e "estou aparando" é sempre um clique, e o que está em
   * vigor se lê sem clicar em nada.
   */
  private stanceRow(): HTMLElement {
    this.stanceButtons.clear();

    const row = document.createElement('div');
    row.className = 'stance-row';
    // Grupo de rádio para quem navega por leitor de tela: sem isto são três botões soltos, e
    // nada diz que escolher um desliga os outros dois.
    row.setAttribute('role', 'radiogroup');
    row.setAttribute('aria-label', 'postura de combate');

    for (const stance of STANCE_ORDER) {
      const profile = STANCES[stance];

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'stance';
      button.textContent = profile.label;
      // A troca escrita por extenso, e não só o nome: "ataque" sozinho não diz que ele cobra
      // defesa, e a decisão que o painel existe para oferecer é justamente o preço.
      button.title = profile.hint;
      button.setAttribute('role', 'radio');

      button.addEventListener('click', () => this.handlers.onStance(stance));

      this.stanceButtons.set(stance, button);
      row.append(button);
    }

    this.paintStance();
    return row;
  }

  /** Acende o botão da postura em vigor e apaga os outros dois. */
  private paintStance(): void {
    for (const [stance, button] of this.stanceButtons) {
      const active = stance === this.stance;
      button.classList.toggle('active', active);
      button.setAttribute('aria-checked', String(active));
    }
  }

  private cell(slot: number): HTMLElement {
    const entry = this.entries.find(
      (candidate) => candidate.parentId === CONTAINER_EQUIPMENT && candidate.slot === slot,
    );

    return itemSlot(
      this.handlers,
      CONTAINER_EQUIPMENT,
      slot,
      entry,
      SLOT_NAMES[slot as ItemSlot] ?? '',
    );
  }
}
